import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../navbar";
import TopNavbar from "../topnavbar";
import Footer from "../footer";

const ROWS_PER_PAGE = 10;
const currentYear   = new Date().getFullYear();
const YEARS         = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

function LoanPayments() {
  const [agency, setAgency] = useState(""); // "Pagibig" | "SSS"

  const [file,       setFile]       = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [previewData,   setPreviewData]   = useState(null);
  const [activeSheet,   setActiveSheet]   = useState(0);
  const [previewing,    setPreviewing]    = useState(false);
  const [confirming,    setConfirming]    = useState(false);

  const [previewPages, setPreviewPages] = useState({});

  // ── Editing ────────────────────────────────────────────────────────────────
  const [isEditing,   setIsEditing]   = useState(false);
  const [editingCell, setEditingCell] = useState(null);

  // ── Upload History ─────────────────────────────────────────────────────────
  const [uploads,      setUploads]      = useState([]);
  const [loadingHist,  setLoadingHist]  = useState(true);
  const [filterMonth, setFilterMonth] = useState("All");
  const [filterYear,   setFilterYear]   = useState("All");

  // ── Messages ───────────────────────────────────────────────────────────────
  const [errorMsg,   setErrorMsg]   = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [warnings,   setWarnings]   = useState([]);

  // ── Toast & Confirm Modal ──────────────────────────────────────────────────
  const [toast,        setToast]        = useState(null);  
  const [confirmModal, setConfirmModal] = useState(null);  

  const fileInputRef = useRef(null);
  const previewRef   = useRef(null);
  const warningsRef  = useRef(null);
  const topRef       = useRef(null);
  const canUpload    = agency !== "";
  const facility     = sessionStorage.getItem("facility") || "";

  const agencyLabel = agency === "Pagibig" ? "Pag-IBIG" : agency === "SSS" ? "SSS" : "";

  useEffect(() => { fetchUploads(); }, []);

  // ── Toast helper ───────────────────────────────────────────────────────────
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUploads = async () => {
    setLoadingHist(true);
    console.log("fetchUploads — facility from session:", JSON.stringify(facility)); // ADD THIS
    try {
      const res = await fetch(`/api/loanpayments/history?facility=${encodeURIComponent(facility)}`);
      if (res.ok) setUploads(await res.json());
      else console.error("fetchUploads failed:", res.status);
    } catch (err) {
      console.error("fetchUploads error:", err);
    } finally {
      setLoadingHist(false);
    }
  };

  const resetFile = () => {
    setFile(null);
    setPreviewData(null);
    setActiveSheet(0);
    setPreviewPages({});
    setErrorMsg("");
    setSuccessMsg("");
    setIsEditing(false);
    setEditingCell(null);
    setWarnings([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetAll = () => { setAgency(""); resetFile(); };

  const handleFile = (selected) => {
    setErrorMsg("");
    setPreviewData(null);
    if (!selected.name.match(/\.xlsx?$/i)) {
      setErrorMsg("Only Excel files (.xlsx, .xls) are accepted.");
      return;
    }
    setFile(selected);
  };

  const handleFileChange = (e) => { if (e.target.files[0]) handleFile(e.target.files[0]); };
  const handleDrop = (e) => {
    e.preventDefault(); setIsDragging(false);
    if (!canUpload) return;
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };
  const handleDragOver  = (e) => { e.preventDefault(); if (canUpload) setIsDragging(true); };
  const handleDragLeave = ()  => setIsDragging(false);

  const handlePreview = async () => {
    if (!agency) { setErrorMsg("Please select a Government Agency."); return; }
    if (!file)   { setErrorMsg("Please attach an Excel file.");       return; }

    setPreviewing(true);
    setErrorMsg("");
    setSuccessMsg("");
    setActiveSheet(0);
    setPreviewPages({});
    setIsEditing(false);
    setEditingCell(null);

    try {
      const formData = new FormData();
      formData.append("file",   file);
      formData.append("agency", agency);

      const res = await fetch("/api/loanpayments/preview", { method: "POST", body: formData });

      if (res.ok) {
        const data = await res.json();
        if (!data.sheets) {
          data.sheets = [{ loanType: null, rows: data.rows ?? [] }];
        }
        setPreviewData(data);
        setWarnings(data.warnings ?? []); 

        setTimeout(() => {
            if (data.warnings?.length > 0) {
            warningsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
            previewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);

      } else {
        const err = await res.json();
        setErrorMsg(err.message || "Preview failed. Please check the file and try again.");
      }
    } catch (err) {
      console.error("Preview error:", err);
      setErrorMsg(`An unexpected error occurred: ${err.message}`);
    } finally {
      setPreviewing(false);
    }
  };

const handleConfirm = async () => {
    const sheets = previewData?.sheets ?? [];
    const hasRows = sheets.some(s => s.rows?.length > 0);
    if (!hasRows) return;

    setConfirming(true);
    setErrorMsg("");

    try {
        const res = await fetch("/api/loanpayments/confirm", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                sheets,                                        
                agency,
                fileName:   previewData.originalFileName,
                filePath:   previewData.savedFilePath,
                employeeId: sessionStorage.getItem("employeeId"),
                facility,
            }),
        });

        if (res.ok) {
            const result = await res.json();
            showToast(`${agencyLabel} payments saved successfully.`);
            setPreviewData(null);
            resetFile();
            fetchUploads();
        } else {
            const err = await res.json();
            showToast(err.message || "Failed to save. Please try again.", "error");
        }
    } catch (err) {
        console.error("Confirm error:", err);
        showToast(`An unexpected error occurred: ${err.message}`, "error");
    } finally {
        setConfirming(false);
    }
};

// Fix handleDelete to use correct key name
const handleDelete = (uploadIds) => {
    setConfirmModal({ uploadIds });  // matches what confirmDelete expects
};

  // ── DELETE: confirmed ──────────────────────────────────────────────────────
  const confirmDelete = async () => {
    const { uploadIds } = confirmModal;
    setConfirmModal(null);
    try {
      // Delete all IDs that belong to this grouped file
      await Promise.all(
        uploadIds.map(id =>
          fetch(`/api/loanpayments/${id}`, { method: "DELETE" })
        )
      );
      showToast("Record deleted successfully.");
      fetchUploads();
    } catch (err) {
      showToast("An unexpected error occurred.", "error");
    }
  };

  const handleCellChange = (sheetIdx, rowIdx, col, value) => {
    const updatedSheets = previewData.sheets.map((sheet, si) => {
      if (si !== sheetIdx) return sheet;
      const updatedRows = [...sheet.rows];
      updatedRows[rowIdx] = { ...updatedRows[rowIdx], [col]: value };
      return { ...sheet, rows: updatedRows };
    });
    setPreviewData(prev => ({ ...prev, sheets: updatedSheets }));
  };

  const getPage = (sheetIdx) => previewPages[sheetIdx] ?? 1;
  const setPage = (sheetIdx, page) => setPreviewPages(prev => ({ ...prev, [sheetIdx]: page }));

  const sheets          = previewData?.sheets ?? [];
  const totalRows       = sheets.reduce((acc, s) => acc + (s.rows?.length ?? 0), 0);
  const activeSheetData = sheets[activeSheet] ?? { loanType: null, rows: [] };
  const activePage      = getPage(activeSheet);
  const activeRows      = activeSheetData.rows ?? [];
  const activeTotalPgs  = Math.max(1, Math.ceil(activeRows.length / ROWS_PER_PAGE));
  const activeStartIdx  = (activePage - 1) * ROWS_PER_PAGE;
  const activePageRows  = activeRows.slice(activeStartIdx, activeStartIdx + ROWS_PER_PAGE);
  const previewColumns  = activeRows.length ? Object.keys(activeRows[0]) : [];

  const groupedUploads = uploads.map(u => ({
      ...u,
      allUploadIds: [u.UploadId]  // always just one now
  }));

  const filteredUploads = groupedUploads.filter(u => {
      if (filterMonth !== "All" && u.UploadDate && String(new Date(u.UploadDate).getMonth() + 1).padStart(2, "0") !== filterMonth) return false;
      if (filterYear  !== "All" && String(new Date(u.UploadDate).getFullYear()) !== filterYear) return false;
      return true;
  });

  const thStyle = {
    backgroundColor: "#4e73df", color: "#fff", fontWeight: "700",
    fontSize: "12px", padding: "10px 12px", textAlign: "center",
    borderBottom: "none", whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "9px 12px", textAlign: "center", fontSize: "12px",
    color: "#5a5c69", verticalAlign: "middle",
    borderTop: "1px solid #e3e6f0", whiteSpace: "nowrap",
  };
  const btnStyle = (bg = "#4e73df", disabled = false) => ({
    backgroundColor: disabled ? "#b7c3ef" : bg,
    color: "#fff", border: "none", borderRadius: "5px",
    padding: "8px 20px", fontSize: "13px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "6px",
  });
  const inputStyle = {
    border: "1px solid #d1d3e2", borderRadius: "5px",
    padding: "7px 12px", fontSize: "13px", color: "#5a5c69",
    outline: "none", width: "100%",
  };

  const loanTypeBadge = (loanType) => {
    const isCalamity = loanType === "Calamity";
    return (
      <span style={{
        background: isCalamity ? "#cfe2ff" : "#d1f2e1",
        color:      isCalamity ? "#084298" : "#0a5c36",
        fontSize: "11px", padding: "2px 9px", borderRadius: "10px", fontWeight: "600",
      }}>
        {loanType ?? "—"}
      </span>
    );
  };

  return (
    <div id="wrapper">
      <Navbar />
      <div id="content-wrapper" className="d-flex flex-column">
        <div id="content">
          <TopNavbar />
          <div className="container-fluid">

            {/* ── Toast Notification ── */}
            {toast && (
              <div style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                zIndex: 99999,
                backgroundColor: toast.type === "error" ? "#e74a3b" : "#1cc88a",
                color: "#fff",
                padding: "14px 20px",
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                fontSize: "13px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                minWidth: "280px",
                animation: "slideIn 0.3s ease",
              }}>
                <i className={`fas ${toast.type === "error" ? "fa-exclamation-circle" : "fa-check-circle"}`}
                  style={{ fontSize: "16px" }}></i>
                <span style={{ flex: 1 }}>{toast.message}</span>
                <button
                  onClick={() => setToast(null)}
                  style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: "16px", lineHeight: 1, padding: 0 }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}

            {/* ── Confirm Delete Modal ── */}
            {confirmModal && (
              <div style={{
                position: "fixed", inset: 0, zIndex: 99998,
                backgroundColor: "rgba(0,0,0,0.45)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div style={{
                  backgroundColor: "#fff", borderRadius: "10px",
                  padding: "32px", width: "380px",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                  textAlign: "center",
                }}>
                  <div style={{ marginBottom: "16px" }}>
                    <i className="fas fa-exclamation-triangle"
                      style={{ fontSize: "44px", color: "#e74a3b" }}></i>
                  </div>
                  <h6 style={{ fontWeight: "700", color: "#2d3748", marginBottom: "8px", fontSize: "16px" }}>
                    Delete Record
                  </h6>
                  <p style={{ fontSize: "13px", color: "#5a5c69", marginBottom: "24px" }}>
                    Are you sure you want to delete this record? This action cannot be undone.
                  </p>
                  <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                    <button style={btnStyle("#858796")} onClick={() => setConfirmModal(null)}>
                      <i className="fas fa-times"></i> Cancel
                    </button>
                    <button style={btnStyle("#e74a3b")} onClick={confirmDelete}>
                      <i className="fas fa-trash"></i> Yes, Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Animations */}
            <style>{`
              @keyframes slideIn {
                from { opacity: 0; transform: translateX(40px); }
                to   { opacity: 1; transform: translateX(0); }
              }
              @keyframes progressSlide {
                0%   { transform: translateX(-100%); }
                50%  { transform: translateX(150%); }
                100% { transform: translateX(150%); }
              }
            `}</style>

            <div  ref={topRef} className="row justify-content-center mb-3">
              <h4 className="m-0 font-weight-bold text-primary header-name">
                Loan Payments Upload
              </h4>
            </div>

            {/* ── Upload Form Card ── */}
            <div className="card shadow mb-4">
              <div className="card-header py-3" style={{ backgroundColor: "#fff", borderBottom: "1px solid #e3e6f0" }}>
                <h6 className="m-0 font-weight-bold text-primary">
                  <i className="fas fa-upload mr-2"></i>Upload Loan Payment File
                </h6>
              </div>
              <div className="card-body">

                {successMsg && (
                  <div style={{ backgroundColor: "#d4edda", border: "1px solid #c3e6cb", borderRadius: "5px", padding: "10px 16px", marginBottom: "16px", color: "#155724", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fas fa-check-circle"></i>{successMsg}
                  </div>
                )}
                {errorMsg && (
                  <div style={{ backgroundColor: "#f8d7da", border: "1px solid #f5c6cb", borderRadius: "5px", padding: "10px 16px", marginBottom: "16px", color: "#721c24", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fas fa-exclamation-circle"></i>{errorMsg}
                  </div>
                )}

                <div style={{ marginBottom: "16px", maxWidth: "320px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: "#5a5c69", marginBottom: "4px", display: "block" }}>
                    Government Agency <span style={{ color: "#e74a3b" }}>*</span>
                  </label>
                  <select value={agency} onChange={(e) => { setAgency(e.target.value); resetFile(); }} style={inputStyle}>
                    <option value="">— Select Agency —</option>
                    <option value="Pagibig">Pag-IBIG</option>
                    <option value="SSS">SSS</option>
                  </select>
                </div>

                <div style={{ fontSize: "12px", color: "#858796", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <i className="fas fa-info-circle" style={{ color: "#4e73df" }}></i>
                  Upload an Excel file with separate sheets for Calamity and Salary loans.
                </div>

                <div
                  onClick={() => canUpload && fileInputRef.current.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  style={{
                    border: `2px dashed ${!canUpload ? "#e0e0e0" : isDragging ? "#4e73df" : "#d1d3e2"}`,
                    borderRadius: "8px", padding: "36px 24px", textAlign: "center",
                    backgroundColor: !canUpload ? "#f5f5f5" : isDragging ? "#eef0fb" : "#f8f9fc",
                    cursor: canUpload ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease", opacity: canUpload ? 1 : 0.6,
                  }}
                >
                  <i className="fas fa-cloud-upload-alt fa-3x"
                    style={{ color: !canUpload ? "#c0c0c0" : "#4e73df", opacity: 0.5, marginBottom: "10px" }}
                  ></i>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#5a5c69" }}>
                    {file ? file.name : "Drag & drop payment file here"}
                  </div>
                  <div style={{ fontSize: "12px", color: "#858796", marginTop: "4px" }}>
                    {file
                      ? `${(file.size / 1024).toFixed(1)} KB — click to replace`
                      : <span>or <span style={{ color: "#4e73df", textDecoration: "underline" }}>click to browse</span> — .xlsx and .xls accepted</span>}
                  </div>
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleFileChange} disabled={!canUpload} />
                </div>

                {file && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px", padding: "10px 14px", backgroundColor: "#f0fff4", borderRadius: "6px", border: "1px solid #c3e6cb" }}>
                    <i className="fas fa-file-excel" style={{ color: "#1cc88a", fontSize: "18px" }}></i>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "13px", fontWeight: "600", color: "#5a5c69" }}>{file.name}</div>
                      <div style={{ fontSize: "11px", color: "#858796" }}>{(file.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <button style={{ background: "none", border: "none", cursor: "pointer", color: "#e74a3b", fontSize: "16px" }} onClick={resetFile}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #e3e6f0", gap: "10px" }}>
                  <button style={btnStyle("#858796")} onClick={resetAll}>
                    <i className="fas fa-undo"></i> Clear
                  </button>
                  <button style={btnStyle("#4e73df", !file || !canUpload || previewing)} onClick={handlePreview} disabled={!file || !canUpload || previewing}>
                    {previewing ? <><i className="fas fa-spinner fa-spin"></i> Extracting...</> : <><i className="fas fa-search"></i> Preview</>}
                  </button>
                </div>
              </div>
            </div>

            {warnings.length > 0 && (
              <div ref={warningsRef} style={{
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "8px",
                  padding: "14px 18px",
                  marginBottom: "16px",
              }}>
                  <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: warnings.length > 1 ? "10px" : 0,
                      fontWeight: "700",
                      fontSize: "13px",
                      color: "#856404",
                  }}>
                      <i className="fas fa-exclamation-triangle"></i>
                      {warnings.length === 1 ? "1 Warning" : `${warnings.length} Warnings`}
                  </div>
                  {warnings.length > 1 && (
                      <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {warnings.map((w, i) => (
                              <li key={i} style={{ fontSize: "12px", color: "#856404", marginBottom: "4px" }}>
                                  {w}
                              </li>
                          ))}
                      </ul>
                  )}
                  {warnings.length === 1 && (
                      <div style={{ fontSize: "12px", color: "#856404" }}>{warnings[0]}</div>
                  )}
              </div>
          )}

            {/* ── Preview Card with Tabs ── */}
            {previewData && sheets.length > 0 && (
              <div className="card shadow mb-4" ref={previewRef}>
                <div className="card-header py-3" style={{ backgroundColor: "#fff", borderBottom: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h6 className="m-0 font-weight-bold text-primary">
                    <i className="fas fa-eye mr-2"></i>
                    Preview — {totalRows} total row{totalRows !== 1 ? "s" : ""} extracted
                  </h6>
                  <button style={btnStyle(isEditing ? "#e74a3b" : "#f6c23e")} onClick={() => { setIsEditing(prev => !prev); setEditingCell(null); }}>
                    <i className={`fas ${isEditing ? "fa-times" : "fa-edit"}`}></i>
                    {isEditing ? " Stop Editing" : " Edit Table"}
                  </button>
                </div>

                {/* Sheet tabs */}
                <div style={{ display: "flex", borderBottom: "1px solid #e3e6f0", padding: "0 20px", backgroundColor: "#fff" }}>
                  {sheets.map((sheet, si) => {
                    const isActive   = si === activeSheet;
                    const isCalamity = sheet.loanType === "Calamity";
                    return (
                      <button key={si} onClick={() => { setActiveSheet(si); setEditingCell(null); }}
                        style={{
                          padding: "10px 20px", fontSize: "13px",
                          fontWeight: isActive ? "700" : "500", border: "none",
                          borderBottom: isActive ? "3px solid #4e73df" : "3px solid transparent",
                          color: isActive ? "#4e73df" : "#858796",
                          backgroundColor: "transparent", cursor: "pointer",
                          display: "flex", alignItems: "center", gap: "8px", transition: "all 0.15s ease",
                        }}
                      >
                        {sheet.loanType ?? `Sheet ${si + 1}`}
                        <span style={{
                          background: isActive ? (isCalamity ? "#cfe2ff" : "#d1f2e1") : "#e3e6f0",
                          color:      isActive ? (isCalamity ? "#084298" : "#0a5c36") : "#858796",
                          fontSize: "11px", padding: "2px 8px", borderRadius: "10px", fontWeight: "600",
                        }}>
                          {sheet.rows?.length ?? 0} rows
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="card-body" style={{ padding: 0 }}>
                  {activeRows.length === 0 ? (
                    <div style={{ padding: "40px", textAlign: "center", color: "#858796", fontSize: "13px" }}>
                      <i className="fas fa-inbox fa-2x" style={{ display: "block", marginBottom: "8px" }}></i>
                      No rows extracted from this sheet.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>{previewColumns.map(col => <th key={col} style={thStyle}>{col}</th>)}</tr>
                        </thead>
                        <tbody>
                          {activePageRows.map((row, idx) => {
                            const globalRowIdx = activeStartIdx + idx;
                            return (
                              <tr key={globalRowIdx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                                {previewColumns.map(col => {
                                  const isThisCell = isEditing && editingCell?.sheetIdx === activeSheet && editingCell?.rowIdx === globalRowIdx && editingCell?.col === col;
                                  if (col === "LoanType") {
                                    return <td key={col} style={tdStyle}>{loanTypeBadge(row[col])}</td>;
                                  }
                                  return (
                                    <td key={col} style={{ ...tdStyle, cursor: isEditing ? "text" : "default" }}
                                      onClick={() => { if (isEditing) setEditingCell({ sheetIdx: activeSheet, rowIdx: globalRowIdx, col }); }}>
                                      {isEditing ? (
                                        <input
                                          readOnly={!isThisCell} autoFocus={isThisCell}
                                          value={row[col] ?? ""}
                                          onChange={e => handleCellChange(activeSheet, globalRowIdx, col, e.target.value)}
                                          onFocus={() => setEditingCell({ sheetIdx: activeSheet, rowIdx: globalRowIdx, col })}
                                          onBlur={() => setEditingCell(null)}
                                          onKeyDown={e => { if (e.key === "Enter" || e.key === "Escape") setEditingCell(null); }}
                                          style={{
                                            border: isThisCell ? "1px solid #4e73df" : "1px solid #e3e6f0",
                                            borderRadius: "4px", padding: "5px 8px", fontSize: "12px", color: "#5a5c69",
                                            width: "100%", minWidth: "80px", textAlign: "center",
                                            backgroundColor: isThisCell ? "#fff" : "transparent",
                                            outline: "none", cursor: "text", boxSizing: "border-box",
                                            boxShadow: isThisCell ? "0 0 0 2px rgba(78,115,223,0.2)" : "none",
                                            transition: "border 0.15s, box-shadow 0.15s",
                                          }}
                                        />
                                      ) : (row[col] ?? "—")}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "16px", borderTop: "1px solid #e3e6f0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button style={btnStyle("#6c757d", activePage === 1)} onClick={() => setPage(activeSheet, activePage - 1)} disabled={activePage === 1}>
                        <i className="fas fa-chevron-left"></i> Previous
                      </button>
                      <span style={{ fontSize: "12px", color: "#5a5c69" }}>
                        Showing {activeStartIdx + 1}–{Math.min(activeStartIdx + ROWS_PER_PAGE, activeRows.length)} of {activeRows.length} rows
                      </span>
                      <button style={btnStyle("#6c757d", activePage === activeTotalPgs)} onClick={() => setPage(activeSheet, activePage + 1)} disabled={activePage === activeTotalPgs}>
                        Next <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button 
                        style={btnStyle("#858796")} 
                        onClick={() => { 
                          setPreviewData(null); 
                          setActiveSheet(0); 
                          setPreviewPages({}); 
                          setIsEditing(false); 
                          setEditingCell(null); 
                          setWarnings([]); 
                          topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                      >
                        <i className="fas fa-times"></i> Cancel
                      </button>
                      <button
                        style={btnStyle("#1cc88a", confirming)}
                            onClick={() => {
                            console.log('=== BUTTON CLICKED ===');
                            handleConfirm();
                        }}
                        disabled={confirming}
                      >
                        {confirming
                          ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                          : <><i className="fas fa-check"></i> Confirm &amp; Save</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {previewData && totalRows === 0 && (
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-circle mr-1"></i>
                No data rows were extracted from the uploaded file. Check that sheet names contain "Calamity" or "Salary".
              </div>
            )}

            {/* ── Upload History Card ── */}
            <div className="card shadow mb-4">
              <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between"
                style={{ backgroundColor: "#fff", borderBottom: "1px solid #e3e6f0" }}>
                <h6 className="m-0 font-weight-bold text-primary">
                  <i className="fas fa-history mr-2"></i>Upload History
                </h6>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ ...inputStyle, width: "130px" }}>
                      <option value="All">All Months</option>
                      <option value="01">January</option>
                      <option value="02">February</option>
                      <option value="03">March</option>
                      <option value="04">April</option>
                      <option value="05">May</option>
                      <option value="06">June</option>
                      <option value="07">July</option>
                      <option value="08">August</option>
                      <option value="09">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                  </select>
                  <select value={filterYear} onChange={e => setFilterYear(e.target.value)} style={{ ...inputStyle, width: "120px" }}>
                    <option value="All">All Years</option>
                    {YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
                  </select>
                  <button style={btnStyle()} onClick={fetchUploads}>
                    <i className="fas fa-sync-alt"></i> Refresh
                  </button>
                </div>
              </div>

              <div className="card-body" style={{ padding: 0 }}>
                {loadingHist ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#858796" }}>
                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                    <div style={{ marginTop: "12px" }}>Loading records...</div>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
                      <thead>
                        <tr>
                          {["#", "Agency", "File Name", "Uploaded By", "Upload Date", "Actions", ""].map(h => (
                            <th key={h} style={thStyle}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUploads.length === 0 ? (
                          <tr>
                            <td colSpan={7} style={{ ...tdStyle, padding: "40px", color: "#858796" }}>
                              <i className="fas fa-inbox fa-2x" style={{ display: "block", marginBottom: "8px" }}></i>
                              No upload records found.
                            </td>
                          </tr>
                        ) : filteredUploads.map((u, idx) => (
                          <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8f9fc" }}>
                            <td style={tdStyle}>{idx + 1}</td>
                            <td style={tdStyle}>{u.Agency === "Pagibig" ? "Pag-IBIG" : u.Agency ?? "—"}</td>
                            <td style={{ ...tdStyle, textAlign: "left", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <i className="fas fa-file-excel mr-1" style={{ color: "#1cc88a" }}></i>
                              {u.FileName ?? "—"}
                            </td>
                            <td style={tdStyle}>{u.UploadedBy ?? "—"}</td>
                            <td style={tdStyle}>
                              {u.UploadDate
                                ? new Date(u.UploadDate).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" })
                                : "—"}
                            </td>
                            <td style={tdStyle}>
                              <button style={btnStyle("#36b9cc")} onClick={async () => {
                                try {
                                  const response = await fetch(`/api/loanpayments/download/${u.UploadId}?facility=${encodeURIComponent(facility)}`);
                                  if (!response.ok) {
                                    const err = await response.json();
                                    showToast(err.message || "Download failed.", "error");
                                    return;
                                  }
                                  const blob = await response.blob();
                                  const url  = window.URL.createObjectURL(blob);
                                  const a    = document.createElement("a");
                                  a.href = url; a.download = u.FileName || "loan_payment.xlsx";
                                  document.body.appendChild(a); a.click(); a.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  console.error("Download error:", err);
                                  showToast("Download failed.", "error");
                                }
                              }}>
                                <i className="fas fa-download"></i> Download
                              </button>
                            </td>
                            <td style={tdStyle}>
                              <button
                                title="Delete"
                                onClick={() => handleDelete(u.allUploadIds)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#adb5bd",
                                  fontSize: "16px",
                                  padding: "4px 8px",
                                  lineHeight: 1,
                                  borderRadius: "4px",
                                  transition: "color 0.15s",
                                }}
                                onMouseEnter={e => e.currentTarget.style.color = "#e74a3b"}
                                onMouseLeave={e => e.currentTarget.style.color = "#adb5bd"}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default LoanPayments;